import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse, ApiError } from '../types/api';
import { CustomLayer } from '../types/customLayer';
import * as geojson from 'geojson';

export const useCustomLayers = (projectId: string | undefined) => {
  const { data, isLoading, error } = useQuery<CustomLayer[], ApiError>({
    queryKey: ['custom_layers', projectId],
    queryFn: () => api.request<CustomLayer[]>(`/api/projects/${projectId}/custom_layers/`),
    enabled: !!projectId,
  });

  return { customLayers: data, isLoading, error };
};

interface CreateCustomLayerData {
  name: string;
  file?: File;
  filestoreFileId?: number;
  color?: string;
  opacity?: number;
}

/**
 * Hook to fetch a single custom layer with its geometries
 */
export interface CustomLayerDetail extends CustomLayer {
  geometries: geojson.FeatureCollection;
}

export const useCustomLayerDetail = (projectId: string | undefined, layerId: number | null) => {
  const { data, isLoading, error } = useQuery<CustomLayerDetail, ApiError>({
    queryKey: ['custom_layer_detail', projectId, layerId],
    queryFn: () => api.request<CustomLayerDetail>(`/api/projects/${projectId}/custom_layers/${layerId}`),
    enabled: !!projectId && layerId !== null,
  });

  return { layerDetail: data, isLoading, error };
};

export const useDeleteCustomLayer = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (layerId: number) =>
      api.delete(`/api/projects/${projectId}/custom_layers/${layerId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom_layers', projectId] });
    },
  });
};

export const useCreateCustomLayer = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<CustomLayer>, ApiError, CreateCustomLayerData>({
    mutationFn: async (data: CreateCustomLayerData) => {
      const formData = new FormData();
      formData.append('name', data.name);
      if (data.file) {
        formData.append('file', data.file);
      } else if (data.filestoreFileId !== undefined) {
        formData.append('filestore_file_id', data.filestoreFileId.toString());
      }
      if (data.color !== undefined) {
        formData.append('color', data.color);
      }
      if (data.opacity !== undefined) {
        formData.append('opacity', data.opacity.toString());
      }

      return await api.request<ApiResponse<CustomLayer>>(
        `/api/projects/${projectId}/custom_layers/`,
        {
          method: 'POST',
          body: formData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom_layers', projectId] });
    },
  });
};

export interface UpdateCustomLayerData {
  layerId: number;
  color?: string;
  opacity?: number;
  name?: string;
}

export const useUpdateCustomLayer = (projectId: string) => {
  const queryClient = useQueryClient();
  const listKey = ['custom_layers', projectId] as const;

  return useMutation<CustomLayer, ApiError, UpdateCustomLayerData, { previous?: CustomLayer[] }>({
    mutationFn: async ({ layerId, color, opacity, name }: UpdateCustomLayerData) => {
      const body: Record<string, unknown> = {};
      if (color !== undefined) body.color = color;
      if (opacity !== undefined) body.opacity = opacity;
      if (name !== undefined) body.name = name;
      return await api.patch<CustomLayer>(
        `/api/projects/${projectId}/custom_layers/${layerId}`,
        body
      );
    },
    onMutate: async ({ layerId, color, opacity, name }) => {
      // Optimistically patch the cache so the map and UI repaint immediately,
      // without triggering a refetch that would unmount the swatch button and
      // detach any open style editor.
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<CustomLayer[]>(listKey);
      if (previous) {
        queryClient.setQueryData<CustomLayer[]>(
          listKey,
          previous.map((cl) =>
            cl.id === layerId
              ? {
                  ...cl,
                  ...(color !== undefined ? { color } : {}),
                  ...(opacity !== undefined ? { opacity } : {}),
                  ...(name !== undefined ? { name } : {}),
                }
              : cl
          )
        );
      }
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
    },
  });
};
